import { apiRoutes } from '@/constants/api-routes';
import { apiRequest } from './http';
import type { BookmarkCheckResult, BookmarkStatus, BookmarkView } from '@/types/api';

/**
 * Matches the server's `CreateBookmarkDto`. `readerId` is a stringified
 * bigint, `bookId` is a UUID.
 */
export interface CreateBookmarkPayload {
  readerId: string;
  bookId: string;
}

/** Matches the server's `CheckBookmarkQueryDto`. */
export interface CheckBookmarkParams {
  readerId: string;
  bookId: string;
}

export const bookmarksService = {
  /** Lists a reader's bookmarks, newest first, each with a full book payload. */
  list(readerId: string) {
    return apiRequest<BookmarkView[]>(apiRoutes.bookmarks.list, {
      query: { readerId },
    });
  },
  /**
   * Idempotent create — the server upserts on (readerId, bookId), so the
   * caller can safely re-issue a POST after an optimistic toggle.
   */
  create(payload: CreateBookmarkPayload) {
    return apiRequest<BookmarkView>(apiRoutes.bookmarks.create, {
      method: 'POST',
      body: payload,
    });
  },
  /** Sets the reading lifecycle status for a saved book. */
  updateStatus(id: string, status: BookmarkStatus) {
    return apiRequest<BookmarkView>(apiRoutes.bookmarks.one(id), {
      method: 'PATCH',
      body: { status },
    });
  },
  remove(id: string) {
    return apiRequest<void>(apiRoutes.bookmarks.one(id), { method: 'DELETE' });
  },
  check(params: CheckBookmarkParams) {
    return apiRequest<BookmarkCheckResult>(apiRoutes.bookmarks.check, {
      query: { readerId: params.readerId, bookId: params.bookId },
    });
  },
};
